import { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Project, ProjectStatusEventBrief, Session } from '@/api/client';
import { api } from '@/api/client';
import { setProjectEntity } from '@/lib/projectEntityCache';
import { Badge } from '@/components/ui/Badge/Badge';
import { CloseIcon, ArrowForwardIcon } from '@/components/ui/Icons';
import { PLANNER_STATES, getPlannerStatusInfo } from '@/utils/plannerStatusInfo';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useTeleport } from '@/contexts/TeleportContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { format, parseISO, isValid } from 'date-fns';
import { formatDecimal } from '@/utils/formatNumber';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import { formatNoEdits, getReviewCheckTypeLabel, REVIEW_NOT_SPECIFIED_LABEL } from '@/utils/reviewCheckType';
import {
  STATUS_EVENT_TYPE_LABELS,
  STATUS_LOG_EDIT_EVENT_ORDER,
  type StatusLogEditEventType,
} from '@/utils/statusEventToState';
import { SESSION_READER_NOT_SET_LABEL } from '@/constants/sessionReader';
import { getProjectTypeText } from '@/utils/projectTypeColors';
import { getProjectTypeBadgeColor } from '@/styles/projectColorTokens';
import '@/components/ProjectSessionsModal/ProjectSessionsModal.css';
import './ProjectDetailsPopover.css';

interface ProjectDetailsPopoverProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  buttonRef: React.RefObject<HTMLElement | null>;
}

function isBlankStr(value: string | null | undefined): boolean {
  return value == null || String(value).trim() === '';
}

function formatDate(value: string | null | undefined, includeTime = false): string {
  if (!value) return '—';
  const date = parseISO(value);
  if (!isValid(date)) return '—';
  return includeTime ? format(date, 'dd.MM.yyyy HH:mm') : format(date, 'dd.MM.yyyy');
}

function pickLatestEventByType(events: ProjectStatusEventBrief[], eventType: string): ProjectStatusEventBrief | null {
  const matches = events.filter((e) => e.event_type === eventType);
  if (matches.length === 0) return null;
  return matches.reduce((a, b) =>
    new Date(a.occurred_at).getTime() > new Date(b.occurred_at).getTime() ? a : b
  );
}

/** Имя и дата/время из project_status_events (дата оформляется отдельно, не жирным) */
function parsePersonEventParts(ev: ProjectStatusEventBrief | null): { name: string; dateTime: string } | null {
  if (!ev?.occurred_at) return null;
  const name = ev.changed_by_name?.trim() || '—';
  const dt = parseISO(ev.occurred_at);
  if (!isValid(dt)) return null;
  return {
    name,
    dateTime: `${format(dt, 'dd.MM.yyyy')} в ${format(dt, 'HH:mm')}`,
  };
}

const MONTAGE_EVENT_ROWS: { label: string; eventType: string }[] = [
  { label: 'Смонтировал', eventType: 'editing_completed' },
  { label: 'Правки подготовил', eventType: 'review_completed' },
  { label: 'Правки записал', eventType: 'corrections_recorded' },
  { label: 'Правки смонтировал', eventType: 'corrections_applied' },
];

const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatDateRu(ymd: string): { dayNum: number; monthName: string } {
  if (!ymd) return { dayNum: 0, monthName: '' };
  const [, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return {
    dayNum: d ?? 0,
    monthName: MONTHS_RU[(m ?? 1) - 1] ?? '',
  };
}

function formatTimeHHMM(t: string | null | undefined): string {
  if (!t) return '—';
  return t.slice(0, 5);
}

/** Хронология: порядок этапов от первого к последнему (обратно к порядку строк редактора дат). */
type HistoryTimelineEventType = StatusLogEditEventType;
const HISTORY_EVENT_ORDER_CHRONO: HistoryTimelineEventType[] = [...STATUS_LOG_EDIT_EVENT_ORDER].reverse();

export function ProjectDetailsPopover({
  open,
  onClose,
  project,
  buttonRef,
}: ProjectDetailsPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { goToSession } = useTeleport();
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [isSessionsExpanded, setIsSessionsExpanded] = useState(false);
  const [expandedReaderId, setExpandedReaderId] = useState<string | null>(null);
  const [plannerAnalysis, setPlannerAnalysis] = useState<{
    status: string;
    message: string;
    remainSessions: number | null;
    plannedSessionsCount: number;
    sessionsPerWeek: number;
  } | null>(null);

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  const queryClient = useQueryClient();
  const { data: fetchedProject, isPending: isDetailLoading } = useQuery({
    queryKey: ['project', project.id, 'details-popover'],
    queryFn: () => api.projects.get(project.id),
    enabled: open && !!project?.id,
    staleTime: 0,
  });

  const displayProject = fetchedProject ?? project;
  /** Планер не показываем и не запрашиваем для архивных проектов */
  const showPlannerInDetails =
    Boolean(!displayProject.is_archived && PLANNER_STATES.has(displayProject.state ?? ''));

  useEffect(() => {
    if (fetchedProject) setProjectEntity(queryClient, fetchedProject);
  }, [fetchedProject, queryClient]);

  useEffect(() => {
    if (!open || !showPlannerInDetails) {
      setPlannerAnalysis(null);
      return;
    }
    api.planner
      .getProjectAnalysis(displayProject.id)
      .then((a) =>
        setPlannerAnalysis({
          status: a.status,
          message: a.message,
          remainSessions: a.remainSessions,
          plannedSessionsCount: a.plannedSessionsCount,
          sessionsPerWeek: a.sessionsPerWeek,
        })
      )
      .catch(() => setPlannerAnalysis(null));
  }, [open, displayProject.id, showPlannerInDetails]);

  usePopoverPositioning({
    triggerRef: buttonRef,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'right',
    preferredPositionY: 'bottom',
    priorityAxis: 'x',
    verticalAlign: 'center',
    offset: 10,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    clampOnScrollFollow: true,
  });

  useEffect(() => {
    if (!open) {
      setExpandedReaderId(null);
      setIsSessionsExpanded(false);
    }
  }, [open]);

  useClickOutside(popoverRef, requestClose, {
    enabled: shouldRender,
    excludeRefs: [buttonRef],
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const readers = displayProject.readers ?? [];
  const authors = displayProject.authors ?? [];
  const producers = displayProject.producers ?? [];
  const techeditors = displayProject.techeditors ?? [];
  const correctors = displayProject.correctors ?? [];
  const contractors = displayProject.contractors ?? [];
  const sessions = displayProject.sessions ?? [];
  const recordedSessions = sessions.filter((s) => s.is_recorded && !s.is_canceled);
  const plannedSessions = sessions.filter(
    (s) => !s.is_recorded && !s.is_canceled && s.session_type !== 'монтаж'
  );
  const totalProgress =
    typeof displayProject.progress === 'number'
      ? displayProject.progress
      : parseFloat(String(displayProject.progress || 0)) || 0;
  const tempoStr =
    showPlannerInDetails &&
    plannerAnalysis?.sessionsPerWeek != null &&
    plannerAnalysis.sessionsPerWeek > 0
      ? `${plannerAnalysis.sessionsPerWeek.toFixed(1)} сес./нед.`
      : '—';

  /** Все возможные этапы по порядку; при отсутствии события — N/A (последнее по времени событие типа) */
  const historyChronologyRows = useMemo(() => {
    const events = (displayProject.status_events ?? []) as ProjectStatusEventBrief[];
    return HISTORY_EVENT_ORDER_CHRONO.map((eventType) => ({
      key: eventType,
      label: STATUS_EVENT_TYPE_LABELS[eventType],
      parts: parsePersonEventParts(pickLatestEventByType(events, eventType)),
    }));
  }, [displayProject.status_events]);

  const montageLines = useMemo(() => {
    const events = (displayProject.status_events ?? []) as ProjectStatusEventBrief[];
    return MONTAGE_EVENT_ROWS.map(({ label, eventType }) => ({
      label,
      parts: parsePersonEventParts(pickLatestEventByType(events, eventType)),
    }));
  }, [displayProject.status_events]);

  const montageSectionEmpty = useMemo(
    () => montageLines.every((row) => row.parts == null),
    [montageLines],
  );

  const hasDeadline = !isBlankStr(displayProject.deadline);
  const performersSectionEmpty =
    readers.length === 0 &&
    producers.length === 0 &&
    correctors.length === 0 &&
    contractors.length === 0 &&
    !hasDeadline;

  const hasPaperPlan =
    Boolean(displayProject.is_backlog) || !isBlankStr(displayProject.paper_book_plan);
  const customersSectionEmpty =
    isBlankStr(displayProject.editor_name) &&
    isBlankStr(displayProject.edition_name) &&
    isBlankStr(displayProject.edition_department_code) &&
    !hasPaperPlan;

  const reviewSectionEmpty =
    techeditors.length === 0 &&
    (displayProject.review_check_type == null || String(displayProject.review_check_type).trim() === '') &&
    isBlankStr(displayProject.review_deadline) &&
    displayProject.corrections_number == null &&
    !displayProject.no_edits;

  const editingFieldsHaveValue =
    (displayProject.time != null && String(displayProject.time).trim() !== '') ||
    displayProject.tracks_count != null ||
    displayProject.size != null;

  const codesFieldsHaveValue =
    (displayProject.asta != null && String(displayProject.asta).trim() !== '') ||
    (displayProject.rin != null && String(displayProject.rin).trim() !== '');

  const technicalSectionHasContent = editingFieldsHaveValue || codesFieldsHaveValue;

  const typeText = getProjectTypeText(displayProject.project_type);
  const typeBadgeColors = typeText ? getProjectTypeBadgeColor(displayProject.project_type) : undefined;

  if (!displayProject) return null;

  const content = (
    <div
      ref={popoverRef}
      style={{ position: 'fixed', left: -9999, top: -9999, pointerEvents: isExiting ? 'none' : 'auto' }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-details-popover ${contentClasses}`}
          role="dialog"
          aria-modal="false"
          aria-labelledby="project-details-title"
        >
      <div className="project-details-popover__inner">
        <div className="project-details-popover__header">
            <div className="project-details-popover__header-content">
            <h2 id="project-details-title" className="project-details-popover__title">
              {typeText && typeBadgeColors ? (
                <Badge variant="custom" bg={typeBadgeColors.bg} color={typeBadgeColors.color}>
                  {typeText}
                </Badge>
              ) : null}
              <span className="project-details-popover__title-text">{displayProject.name}</span>
            </h2>
            <p className="project-details-popover__subtitle">
              {authors.length > 0 ? (
                authors.map((a) => a.name).join(', ')
              ) : (
                <span className="project-details-popover__value-na">N/A</span>
              )}
            </p>
          </div>
          <IconButton
            variant="danger"
            size="m"
            iconSize={20}
            onClick={requestClose}
            className="project-details-popover__close"
            aria-label="Закрыть"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <OverlayScrollArea
          className="project-details-popover__body"
          osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
        >
          <div className="project-details-popover__scroll-content">
          {isDetailLoading && !fetchedProject && (
            <div className="project-details-popover__detail-loading" role="status">
              Загрузка сессий и деталей…
            </div>
          )}
          {showPlannerInDetails && plannerAnalysis && (
            <div className="project-details-popover__planner-section">
              <div className="project-details-popover__planner-badge-wrap">
                <Badge
                  variant="custom"
                  bg={getPlannerStatusInfo(plannerAnalysis.status).badgeBg}
                  color={getPlannerStatusInfo(plannerAnalysis.status).badgeColor}
                >
                  {getPlannerStatusInfo(plannerAnalysis.status).label}
                </Badge>
              </div>
              {plannerAnalysis.message && (
                <div
                  className={`project-details-popover__planner-alert project-details-popover__planner-alert_${getPlannerStatusInfo(plannerAnalysis.status).severity}`}
                >
                  <span className="project-details-popover__planner-alert-icon">
                    {(() => {
                      const { icon: Icon } = getPlannerStatusInfo(plannerAnalysis.status);
                      return <Icon size={20} />;
                    })()}
                  </span>
                  <span className="project-details-popover__planner-alert-text">{plannerAnalysis.message}</span>
                </div>
              )}
            </div>
          )}
          {!performersSectionEmpty && (
          <section className="project-details-popover__section">
            <h3 className="project-details-popover__section-title">Исполнители</h3>
            <div className="project-details-popover__card">
              <InfoRow label="Чтецы" value={readers.map((r) => r.name).join(', ') || '—'} />
              <InfoRow label="Продюсеры" value={producers.map((p) => p.name).join(', ') || '—'} />
              <InfoRow label="Корректоры" value={correctors.map((c) => c.name).join(', ') || '—'} />
              <InfoRow
                label="Контрагенты"
                value={
                  contractors.length > 0
                    ? contractors
                        .map((c) =>
                          c.service_name
                            ? `${contractorDisplayName(c)} — ${c.service_name}`
                            : contractorDisplayName(c)
                        )
                        .join(', ')
                    : '—'
                }
              />
              <InfoRow label="Дедлайн" value={hasDeadline ? formatDate(displayProject.deadline) : undefined} />
            </div>
          </section>
          )}

          {!customersSectionEmpty && (
          <section className="project-details-popover__section">
            <h3 className="project-details-popover__section-title">Заказчики</h3>
            <div className="project-details-popover__card">
              <InfoRow label="Ответственный редактор" value={displayProject.editor_name ?? '—'} />
              <InfoRow label="Редакция" value={displayProject.edition_name ?? '—'} />
              <InfoRow label="Подразделение" value={displayProject.edition_department_code ?? '—'} />
              <InfoRow
                label="План бумаги"
                value={
                  displayProject.is_backlog
                    ? 'Бэклог'
                    : displayProject.paper_book_plan?.trim()
                      ? formatDate(displayProject.paper_book_plan)
                      : undefined
                }
              />
            </div>
          </section>
          )}

          {!montageSectionEmpty && (
          <section className="project-details-popover__section">
            <h3 className="project-details-popover__section-title">Монтаж</h3>
            <div className="project-details-popover__card">
              {montageLines.map((row) => (
                <InfoRow key={row.label} label={row.label} variant="montage" montageParts={row.parts} />
              ))}
            </div>
          </section>
          )}

          {!reviewSectionEmpty && (
          <section className="project-details-popover__section">
            <h3 className="project-details-popover__section-title">Проверка</h3>
            <div className="project-details-popover__card">
              <InfoRow label="Техредакторы" value={techeditors.map((t) => t.name).join(', ') || '—'} />
              <InfoRow label="Тип проверки" value={getReviewCheckTypeLabel(displayProject.review_check_type) ?? REVIEW_NOT_SPECIFIED_LABEL} />
              <InfoRow label="Дедлайн проверки" value={displayProject.review_deadline ? formatDate(displayProject.review_deadline) : REVIEW_NOT_SPECIFIED_LABEL} />
              <InfoRow
                label="Количество правок"
                value={displayProject.corrections_number != null ? String(displayProject.corrections_number) : undefined}
              />
              <InfoRow label="Без правок" value={formatNoEdits(displayProject.no_edits)} />
            </div>
          </section>
          )}

          {/* Прогресс записи: список таблицы без сессий — данные подгружаем GET /projects/:id */}
          {readers.length > 0 &&
            totalProgress < 100 &&
            displayProject.project_type !== 'Podcast' && (
            <section className="project-details-popover__section">
              <h3 className="project-details-popover__section-title">Прогресс записи</h3>
              <div className="project-details-popover__progress-details">
                {readers.map((r) => {
                  const hasReader = (s: Session) =>
                    s.readers?.some((reader) => reader.id === r.id) || s.reader_id === r.id;
                  const readerRecorded = recordedSessions.filter(hasReader);
                  const readerPlanned = plannedSessions.filter(hasReader);
                  const readerPlannedCount = readerPlanned.reduce((sum, s) => sum + (s.coef ?? 1), 0);
                  const readerRemainingStr =
                    r.remaining_sessions != null && r.remaining_sessions !== ''
                      ? formatDecimal(r.remaining_sessions)
                      : showPlannerInDetails && plannerAnalysis?.remainSessions != null
                        ? formatDecimal(plannerAnalysis.remainSessions)
                        : '—';
                  const readerPagesRead = r.pages_read ?? 0;
                  const readerAvg =
                    r.avg_pages_per_hour != null
                      ? formatDecimal(r.avg_pages_per_hour)
                      : '—';
                  const isExpanded = expandedReaderId === r.id;
                  return (
                    <div key={r.id} className="project-details-popover__reader-progress">
                      <button
                        type="button"
                        className="project-details-popover__reader-progress-trigger"
                        onClick={() => setExpandedReaderId(isExpanded ? null : r.id)}
                      >
                        <div className="project-details-popover__reader-progress-header">
                          <span className="project-details-popover__progress-reader-name">{r.name}</span>
                        </div>
                        <ProgressBar progress={r.progress ?? 0} />
                        {isExpanded && (
                          <div className="project-details-popover__progress-stats">
                            <div className="project-details-popover__progress-stats-row">
                              <span>Проведено сессий: {readerRecorded.length}</span>
                              <span>Осталось: {readerRemainingStr}</span>
                              <span>Запланировано: {readerPlannedCount}</span>
                            </div>
                            <div className="project-details-popover__progress-stats-row">
                              <span>Прочитано: {readerPagesRead > 0 ? readerPagesRead : '—'} стр.</span>
                              <span>В среднем: {readerAvg === '—' ? '—' : `${readerAvg} стр/ч`}</span>
                              <span>Необходимый темп: {tempoStr}</span>
                            </div>
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Запланированные сессии — по дате, от самой ранней */}
          {plannedSessions.length > 0 && (() => {
            const sortedPlanned = [...plannedSessions].sort((a, b) => {
              const d = (a.date ?? '').localeCompare(b.date ?? '');
              if (d !== 0) return d;
              return (a.start ?? '').localeCompare(b.start ?? '');
            });
            const getReaderDisplay = (session: Session) => {
              const sessionReaders = session.readers?.length
                ? session.readers.map((r) => r.name)
                : session.reader_id
                  ? [readers.find((r) => r.id === session.reader_id)?.name ?? '—']
                  : [];
              if (sessionReaders.length === 0) {
                return (
                  <span className="project-details-popover__planned-reader project-details-popover__planned-reader_unset">
                    {SESSION_READER_NOT_SET_LABEL}
                  </span>
                );
              }
              return <span className="project-details-popover__planned-reader">{sessionReaders.join(', ')}</span>;
            };
            return (
              <section className="project-details-popover__section">
                <h3 className="project-details-popover__section-title">Запланированные сессии</h3>
                <button
                  type="button"
                  className="project-details-popover__expand-btn"
                  onClick={() => setIsSessionsExpanded(!isSessionsExpanded)}
                >
                  {isSessionsExpanded ? 'Свернуть запланированные сессии' : 'Развернуть запланированные сессии'}
                </button>
                {isSessionsExpanded && (
                <div className="project-details-popover__card">
                  <ul className="project-details-popover__sessions project-sessions-modal__list">
                    {sortedPlanned.map((s) => {
                      const { dayNum, monthName } = formatDateRu(s.date ?? '');
                      const readerDisplay = getReaderDisplay(s);
                      return (
                        <li key={s.id} className="project-sessions-modal__card project-sessions-modal__card_planned">
                          <div className="project-sessions-modal__card-meta-row">
                            <span className="project-sessions-modal__card-meta project-details-popover__planned-meta">
                              {readerDisplay}
                              <span className="project-sessions-modal__card-date project-details-popover__planned-datetime">
                                <span className="project-sessions-modal__card-date-line1">
                                  <strong>{dayNum} {monthName}</strong>
                                </span>
                                <span className="project-sessions-modal__card-date-line2">
                                  с {formatTimeHHMM(s.start)} до {formatTimeHHMM(s.end)}
                                </span>
                              </span>
                            </span>
                            <IconButton
                              aria-label="Перейти к сессии"
                              variant="default"
                              size="m"
                              iconSize={18}
                              onClick={(e) => {
                                e.stopPropagation();
                                requestClose();
                                goToSession({ id: s.id, date: s.date ?? '' });
                              }}
                              title="Перейти к сессии"
                              className="project-sessions-modal__card-goto"
                            >
                              <ArrowForwardIcon />
                            </IconButton>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                )}
              </section>
            );
          })()}

          {/* Техническая информация */}
          {technicalSectionHasContent && (
            <section className="project-details-popover__section">
              <h3 className="project-details-popover__section-title">Техническая информация</h3>
              <div className="project-details-popover__card">
                {editingFieldsHaveValue && (
                  <>
                    <InfoRow label="Время" value={displayProject.time ?? '—'} />
                    <InfoRow label="Количество треков" value={displayProject.tracks_count != null ? String(displayProject.tracks_count) : '—'} />
                    <InfoRow label="Размер" value={displayProject.size != null ? `${displayProject.size} Гб` : undefined} />
                  </>
                )}
                {codesFieldsHaveValue && (
                  <>
                    {editingFieldsHaveValue && <hr className="project-details-popover__divider" />}
                    <InfoRow label="ASTA" value={displayProject.asta ?? '—'} />
                    <InfoRow label="РИН" value={displayProject.rin ?? '—'} />
                  </>
                )}
              </div>
            </section>
          )}

          {/* Хронология: полный список типов событий; данные из project_status_events или N/A */}
          <section className="project-details-popover__section">
            <h3 className="project-details-popover__section-title">История состояний</h3>
            <button
              type="button"
              className="project-details-popover__expand-btn"
              onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            >
              {isHistoryExpanded ? 'Свернуть хронологию событий' : 'Развернуть хронологию событий'}
            </button>
            {isHistoryExpanded && (
              <div className="project-details-popover__card">
                {historyChronologyRows.map((row) => (
                  <InfoRow
                    key={row.key}
                    label={row.label}
                    variant="montage"
                    montageParts={row.parts}
                    montageShowName={false}
                  />
                ))}
              </div>
            )}
          </section>
          </div>
        </OverlayScrollArea>
      </div>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  if (!shouldRender) return null;
  return createPortal(content, document.body);
}

const EMPTY_FIELD = 'N/A';

function formatInfoValue(value: string | undefined | null): string {
  if (value == null || value === '' || value === '—' || value === 'Не указано') return EMPTY_FIELD;
  return value;
}

function InfoRow({
  label,
  value,
  variant = 'default',
  montageParts,
  montageShowName = true,
}: {
  label: string;
  value?: string | undefined | null;
  /** montage: имя (medium) + дата/время (regular); без данных → N/A */
  variant?: 'default' | 'montage';
  montageParts?: { name: string; dateTime: string } | null;
  /** false — только дата и время (хронология); true — блок «Монтаж» с именем */
  montageShowName?: boolean;
}) {
  if (variant === 'montage') {
    const hasParts = montageParts != null;
    return (
      <div className="project-details-popover__info-row">
        <span className="project-details-popover__info-label">{label}</span>
        <span className="project-details-popover__info-value project-details-popover__info-value_montage">
          {hasParts ? (
            montageShowName ? (
              <>
                <span className="project-details-popover__montage-name">{montageParts.name}</span>
                <span className="project-details-popover__montage-sep"> • </span>
                <span className="project-details-popover__montage-datetime">{montageParts.dateTime}</span>
              </>
            ) : (
              <span className="project-details-popover__montage-datetime">{montageParts.dateTime}</span>
            )
          ) : (
            <span className="project-details-popover__value-na">{EMPTY_FIELD}</span>
          )}
        </span>
      </div>
    );
  }

  const display = formatInfoValue(value);
  return (
    <div className="project-details-popover__info-row">
      <span className="project-details-popover__info-label">{label}</span>
      <span className="project-details-popover__info-value">
        {display === EMPTY_FIELD ? (
          <span className="project-details-popover__value-na">{EMPTY_FIELD}</span>
        ) : (
          display
        )}
      </span>
    </div>
  );
}

function ProgressBar({ progress }: { progress: number }) {
  const pct = Math.min(Math.max(progress || 0, 0), 100);
  return (
    <div className="project-details-popover__progress-row">
      <div
        className="project-details-popover__progress-bar"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="project-details-popover__progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="project-details-popover__progress-pct">{Math.round(pct)}%</span>
    </div>
  );
}
